// frontend/src/pages/crm/LeadActivityPage.tsx
// Lead Activity tab (2026-07-29) — the per-employee personal lead list, web.
//
// Spec: docs/superpowers/specs/2026-07-29-lead-activity-tab-design.md
//
// One list of MY leads, three source chips, split actioned / not-actioned,
// colour-banded by how long an untouched lead has been sitting, grouped by day
// with a sticky day header. Read-only — every row deep-links into the existing
// lead detail. There is no owner filter and no team toggle by design: this is a
// personal view, and the backend pins the query to the signed-in user.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/utils/api';

type Source = 'assigned' | 'created' | 'field';
type Band = 'green' | 'amber' | 'red';

type Row = {
  id: string;
  name: string | null;
  company: string | null;
  phone: string | null;
  location: string | null;
  product: string | null;
  stage: string | null;
  interestLevel: string | null;
  score: number | null;
  fieldActivityStatus: string | null;
  sources: Source[];
  arrivedAt: string;
  actioned: boolean;
  band: Band;
};

type DayGroup = {
  date: string;
  count: number;
  notActionedCount: number;
  rows: Row[];
};

type Payload = {
  sources: Source[];
  fieldLeadsAvailable: boolean;
  totals: { total: number; actioned: number; notActioned: number };
  truncated: boolean;
  groups: DayGroup[];
};

/** Same green/amber/red tones the field-activity board + hot clock use. */
const BAND_COLOR: Record<Band, string> = {
  green: '#3AA06B',
  amber: '#E0A02E',
  red: '#D5433B',
};

const BAND_LABEL: Record<Band, string> = {
  green: 'On time',
  amber: 'Ageing 1–2 working days',
  red: 'Untouched over 2 working days',
};

const SOURCE_LABEL: Record<Source, string> = {
  assigned: 'Assigned to me',
  created: 'Created by me',
  field: 'Field leads',
};

const ALL_SOURCES: Source[] = ['assigned', 'created', 'field'];

/** Local 'YYYY-MM-DD' for the <input type="date"> controls. */
function todayKey(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function prettyDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const label = dt.toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
  if (key === todayKey()) return `Today · ${label}`;
  return label;
}

export function LeadActivityPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Payload | null>(null);

  // Default view: Today. A range whose ends are equal IS the single-date case,
  // so there is exactly one control pair, not two widgets (spec).
  const [from, setFrom] = useState<string>(todayKey());
  const [to, setTo] = useState<string>(todayKey());
  const [sources, setSources] = useState<Source[]>(ALL_SOURCES);
  const [actionedFilter, setActionedFilter] = useState<'all' | 'actioned' | 'not'>('all');

  const fieldLeadsAvailable = data?.fieldLeadsAvailable ?? true;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/lead-activity', {
        params: { sources: sources.join(','), from, to },
      });
      setData(res.data?.data ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not load lead activity');
    } finally {
      setLoading(false);
    }
  }, [sources, from, to]);

  useEffect(() => { load(); }, [load]);

  const toggleSource = (s: Source) => {
    setSources((prev) => {
      const next = prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s];
      // Never let the user reach an empty selection — that reads as a bug, not a filter.
      return next.length === 0 ? prev : next;
    });
  };

  const groups = useMemo<DayGroup[]>(() => {
    const all = data?.groups ?? [];
    if (actionedFilter === 'all') return all;
    const want = actionedFilter === 'actioned';
    return all
      .map((g) => {
        const rows = g.rows.filter((r) => r.actioned === want);
        return { ...g, rows, count: rows.length, notActionedCount: rows.filter((r) => !r.actioned).length };
      })
      .filter((g) => g.rows.length > 0);
  }, [data, actionedFilter]);

  const totals = data?.totals ?? { total: 0, actioned: 0, notActioned: 0 };

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Lead Activity</h1>
      <p style={{ color: '#6b7280', marginTop: 6, fontSize: 13 }}>
        Everything that came to you, everything you created — and what you have actually
        touched. Untouched leads age from green to red over two working days.
      </p>

      {/* ── Controls ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '16px 0 8px' }}>
        {ALL_SOURCES.map((s) => {
          const disabled = s === 'field' && !fieldLeadsAvailable;
          const on = sources.includes(s) && !disabled;
          return (
            <button
              key={s}
              type="button"
              onClick={() => !disabled && toggleSource(s)}
              disabled={disabled}
              title={disabled ? 'Field leads are not available on this deployment yet.' : undefined}
              aria-pressed={on}
              style={{
                border: `1px solid ${on ? '#6B7FD6' : '#d1d5db'}`,
                background: on ? '#6B7FD614' : '#fff',
                color: disabled ? '#9ca3af' : on ? '#4453A6' : '#374151',
                borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {SOURCE_LABEL[s]}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
          From
          <input
            type="date" value={from} max={todayKey()}
            onChange={(e) => {
              const v = e.target.value;
              setFrom(v);
              if (v > to) setTo(v); // keep the range valid; equal ends == single day
            }}
            style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 8px' }}
          />
        </label>
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
          To
          {/* max: today — matches the mobile picker's `lastDate`, so neither
              client can ask for a window that cannot hold a lead yet. */}
          <input
            type="date" value={to} min={from} max={todayKey()}
            onChange={(e) => setTo(e.target.value)}
            style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 8px' }}
          />
        </label>
        <button
          type="button"
          onClick={() => { setFrom(todayKey()); setTo(todayKey()); }}
          style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}
        >
          Today
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {([
          ['all', `All ${totals.total}`],
          ['actioned', `Actioned ${totals.actioned}`],
          ['not', `Not actioned ${totals.notActioned}`],
        ] as ['all' | 'actioned' | 'not', string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActionedFilter(key)}
            aria-pressed={actionedFilter === key}
            style={{
              border: `1px solid ${actionedFilter === key ? '#374151' : '#e5e7eb'}`,
              background: actionedFilter === key ? '#374151' : '#fff',
              color: actionedFilter === key ? '#fff' : '#374151',
              borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Body ─────────────────────────────────────────────────── */}
      {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}
      {error && <div style={{ color: '#b91c1c' }}>{error}</div>}

      {!loading && !error && data?.truncated && (
        <div style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
          Showing the first 1000 leads for this range — narrow the dates to see the rest.
        </div>
      )}

      {!loading && !error && groups.length === 0 && (
        <div style={{ color: '#6b7280', padding: '48px 0', textAlign: 'center' }}>
          Nothing for this selection. Try a wider date range or turn a chip back on.
        </div>
      )}

      {!loading && !error && groups.map((group) => (
        <section key={group.date} style={{ marginBottom: 22 }}>
          <div
            style={{
              position: 'sticky', top: 0, zIndex: 1,
              background: '#fff', borderBottom: '1px solid #e5e7eb',
              padding: '8px 2px', display: 'flex', alignItems: 'baseline', gap: 10,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 14 }}>{prettyDay(group.date)}</span>
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              {group.count} lead{group.count === 1 ? '' : 's'}
            </span>
            {group.notActionedCount > 0 && (
              <span style={{ color: '#B45309', fontSize: 13, fontWeight: 600 }}>
                {group.notActionedCount} not actioned
              </span>
            )}
          </div>

          {group.rows.map((row) => (
            <Link
              key={row.id}
              to={`/leads/${row.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit',
                border: '1px solid #e5e7eb', borderLeft: `4px solid ${BAND_COLOR[row.band]}`,
                borderRadius: 10, padding: 12, marginTop: 8,
              }}
            >
              <span
                title={BAND_LABEL[row.band]}
                aria-label={BAND_LABEL[row.band]}
                style={{ width: 10, height: 10, borderRadius: '50%', background: BAND_COLOR[row.band], flex: '0 0 auto' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{row.name || row.company || 'Unnamed lead'}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  {[
                    row.company && row.company !== row.name ? row.company : null,
                    row.phone || 'No number',
                    row.location || null,
                    row.product || null,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: '0 0 auto' }}>
                {row.sources.map((s) => (
                  <span
                    key={s}
                    style={{
                      fontSize: 11, fontWeight: 600, color: '#4453A6', background: '#6B7FD614',
                      borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap',
                    }}
                  >
                    {SOURCE_LABEL[s]}
                  </span>
                ))}
                <span
                  style={{
                    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                    color: row.actioned ? '#3AA06B' : '#B45309',
                    background: row.actioned ? '#3AA06B14' : '#E0A02E1f',
                    borderRadius: 6, padding: '3px 8px',
                  }}
                >
                  {row.actioned ? 'Actioned' : 'Not actioned'}
                </span>
              </div>
            </Link>
          ))}
        </section>
      ))}
    </div>
  );
}

export default LeadActivityPage;
